import classNames from 'classnames';
import styles from './index.module.scss';
import {
  ExclamationCircleFilled,
  ExclamationCircleOutlined,
  InfoCircleFilled,
  CloseCircleOutlined,
  SafetyOutlined,
} from '@ant-design/icons';
import { useEffect, useState } from 'react';
import {
  IAPICompanyFullInfo,
  CommonStatusStep,
  MDMAuthStatus,
  IAuditItem,
  StudioCertifiedType,
} from '../../../model/api-modules';
import { Radio, Spin } from 'antd';
import {
  getKpiCodeList,
  submitCompanyInfo,
  updateCompanyInfo,
} from '../../../api';
import { useSelector } from 'react-redux';
import { IRootState } from '../../../store/reducers';
import { messageError } from '../../../components/message';
import { ICompanyFormInfo, ICompanyRejectType } from '../../../model';
import { getCompanyFileUrl } from '../lib/company-cos';
import { strToJson } from '../../../lib';
import { CompanyRejectTypeLabelMap } from '../../../config/company';
import loadingIcon from '../../../assets/svg/loading-icon.svg';
import getFormatMessage from '../../../locales/get-format-message';
import { APIError } from '../../../lib/http/http';
import { getConfig } from '../../../config';
import { ModalWrap } from '../../../components/modal-wrap';
import { IMidasAccountInfo } from '../../../model/billing-center';
import { auditWriteLog } from '../../lib/aduit-log';
import { getCertInfoText, getCertTypeOptions, renderAuthButton } from './lib';
import { CompanyInfoViewPanel } from './company-info-panel';
import { CompanyAuthDrawer } from './company-drawer';
import { CommonPrompt } from '../../../components/prompt';
import { CommonTag } from '../../../components/tag';

interface IProps {
  readonly onReload: () => void;
  readonly companyInfo?: IAPICompanyFullInfo | null;
  readonly accountInfo?: IMidasAccountInfo | null;
}

export const CompanyAuth = (props: IProps) => {
  const { companyInfo, accountInfo } = props;
  const selectStudio = useSelector(
    (state: IRootState) => state.studio.selectStudio
  );

  const [businessLicenseUrl, setBusinessLicenseUrl] = useState<string>('');
  const [showAuthDrawer, setShowAuthDrawer] = useState<boolean>(false);
  const [certType, setCertType] = useState<StudioCertifiedType | undefined>(
    companyInfo?.lastCertifiedType
  );
  const [kpiOptions, setKpiOptions] = useState<
    { label: string; value: string }[]
  >([]);

  useEffect(() => {
    init();
  }, [companyInfo, selectStudio?.studioID]);

  const init = async () => {
    // 获取kpi code列表
    const kpiCodeList = await getKpiCodeList();
    setKpiOptions(
      kpiCodeList.map((item) => ({
        label: `${item.name}(${item.code})`,
        value: item.code,
      }))
    );
    if (!selectStudio || !companyInfo) {
      return;
    }

    const businessLicenseUrl = await getCompanyFileUrl(
      companyInfo.BusinessLicenseImageUrl
    );
    setBusinessLicenseUrl(businessLicenseUrl);
  };

  const submit = async (info?: ICompanyFormInfo) => {
    if (!selectStudio) {
      return;
    }

    if (!info) {
      props.onReload();
      setShowAuthDrawer(false);
      return;
    }
    let res: boolean | APIError | undefined = false;

    const adminApplyDomain = getConfig().supportURL || location.origin;
    const adminApplyLink = `${adminApplyDomain}/admin/company-auth`;

    if (companyInfo?.Name) {
      res = await updateCompanyInfo(companyInfo.id, {
        name: info.name,
        description: info.description,
        creditCode: info.creditCode,
        businessLicenseImageUrl: info.uploadValue.fileName || '',
        contacts: `${info.contactName}|${info.contactIDCode}|${info.contactPhone}`,
        studioID: selectStudio?.studioID,
        adminApplyLink,
      });
      auditWriteLog(IAuditItem.EditStudioCertification, {
        detail: {
          name: info.name,
          description: info.description,
          creditCode: info.creditCode,
          businessLicenseImageUrl: info.uploadValue.fileName || '',
          contacts: `${info.contactName}|${info.contactIDCode}|${info.contactPhone}`,
          studioID: selectStudio?.studioID,
          adminApplyLink: `${getConfig().supportURL}/admin/company-auth`,
        },
      });
    } else {
      res = await submitCompanyInfo({
        name: info.name,
        description: info.description,
        creditCode: info.creditCode,
        businessLicenseImageUrl: info.uploadValue.fileName || '',
        contacts: `${info.contactName}|${info.contactIDCode}|${info.contactPhone}`,
        studioID: selectStudio?.studioID,
        adminApplyLink,
      });
      auditWriteLog(IAuditItem.SubmitStudioCertification, {
        detail: {
          name: info.name,
          description: info.description,
          creditCode: info.creditCode,
          businessLicenseImageUrl: info.uploadValue.fileName || '',
          contacts: `${info.contactName}|${info.contactIDCode}|${info.contactPhone}`,
          studioID: selectStudio?.studioID,
          adminApplyLink: `${getConfig().supportURL}/admin/company-auth`,
        },
      });
    }

    if (res) {
      init();
      props.onReload();
      setShowAuthDrawer(false);
      return;
    }
    messageError('提交认证失败');
  };

  const renderStatus = () => {
    if (companyInfo === undefined) {
      return null;
    }
    const statusMap = {
      [CommonStatusStep.Pending]: (
        <>
          <span>{getFormatMessage('company_status_pending')}</span>
          <img src={loadingIcon} className={styles.loadingIcon} />
        </>
      ),
      [CommonStatusStep.Resolve]: (
        <>
          <span>{getFormatMessage('company_status_resolve')}</span>
          <SafetyOutlined />
        </>
      ),
      [CommonStatusStep.Reject]: (
        <>
          <span>{getFormatMessage('company_status_reject')}</span>
          <CloseCircleOutlined />
        </>
      ),
    };

    if (!companyInfo || companyInfo.Status === undefined) {
      return (
        <>
          <span>{getFormatMessage('company_no_auth')}</span>
          <ExclamationCircleOutlined />
        </>
      );
    }

    return statusMap[companyInfo.Status];
  };

  const renderContent = () => {
    if (companyInfo === undefined) {
      return null;
    }

    if (!companyInfo) {
      return (
        <div className={classNames(styles.content)} style={{ paddingLeft: 0 }}>
          <div>{getFormatMessage('studio_auth_type')}</div>
          <div>
            <Radio.Group
              value={certType}
              onChange={(val) => setCertType(val.target.value)}
              options={getCertTypeOptions()}
            />
          </div>
          <CommonPrompt type="warn">
            <span
              dangerouslySetInnerHTML={{
                __html: getCertInfoText(certType),
              }}
            ></span>
          </CommonPrompt>
        </div>
      );
    }

    const status = companyInfo.Status;
    if (status === CommonStatusStep.Pending) {
      return (
        <div className={classNames(styles.content)}>
          <InfoCircleFilled />
          <span>{getFormatMessage('company_auth_info')}</span>
        </div>
      );
    }

    if (status === CommonStatusStep.Reject) {
      const rejectArray =
        strToJson<Array<any>>(companyInfo.RejectReason || '') || [];

      let rejectItemLabels = rejectArray.map((rejectItem) => {
        return (
          CompanyRejectTypeLabelMap[rejectItem.type as ICompanyRejectType] +
          getFormatMessage('company_reject_text')
        );
      });

      if (
        !rejectItemLabels.length &&
        companyInfo.MDMStatus === MDMAuthStatus.ApprovalFailed
      ) {
        const mdmRejectReason =
          strToJson<{ code?: number; msg?: string }>(
            companyInfo.MDMRejectReason || ''
          ) || {};
        rejectItemLabels = [
          getFormatMessage(String(mdmRejectReason.code || '')),
        ];
      }

      return (
        <div className={classNames(styles.content)}>
          <ExclamationCircleFilled />
          <div>
            <span>{getFormatMessage('company_reject_title')}:</span>
            <div>
              <div className={styles.rejectReasons}>
                {rejectItemLabels.map((labelText) => {
                  return (
                    <div className={styles.rejectReasonItem}>
                      <span className={styles.dot}></span>
                      <span>{`${labelText}`}</span>
                    </div>
                  );
                })}
                {
                  // 暂时去掉
                  /*
                  <div className={styles.viewDoc}>
                    <Button type="link">
                      <span>
                        <img src={bookIcon} />
                        {getFormatMessage('company_view_doc')}
                      </span>
                    </Button>
                  </div>
                  */
                }
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (status === CommonStatusStep.Resolve) {
      return (
        <div
          className={classNames(styles.content, {
            [styles.internalContent]:
              companyInfo.certifiedType === StudioCertifiedType.InternalKpiCode,
          })}
        >
          <CompanyInfoViewPanel
            certUrl={businessLicenseUrl}
            data={companyInfo}
          />
        </div>
      );
    }

    return <></>;
  };

  const renderAuthDrawer = () => {
    if (!showAuthDrawer || !certType) {
      return;
    }

    return (
      <CompanyAuthDrawer
        certType={certType}
        businessLicenseUrl={businessLicenseUrl}
        data={companyInfo}
        kpiCodeOptions={kpiOptions}
        onClose={(info?: ICompanyFormInfo) => {
          if (!info) {
            setShowAuthDrawer(false);
            return;
          }
          if (
            (!companyInfo && Object.values(info).some((val) => !!val)) ||
            String(info.name || '') !== String(companyInfo?.Name || '') ||
            String(info.description || '') !==
              String(companyInfo?.Description || '') ||
            String(info.creditCode || '') !==
              String(companyInfo?.CreditCode || '') ||
            String(info.uploadValue?.fileName || '') !==
              String(companyInfo?.BusinessLicenseImageUrl || '') ||
            `${info.contactName || ''}|${info.contactIDCode || ''}|${
              info.contactPhone || ''
            }` !== String(companyInfo?.Contacts || '||')
          ) {
            ModalWrap.warn({
              width: '440px',
              className: 'delete-env-modal delete-modal',
              title: getFormatMessage('discard_changes'),
              oKText: getFormatMessage('Confirm'),
              cancelText: getFormatMessage('cancel'),
              children: (
                <div style={{ whiteSpace: 'pre-line' }}>
                  {getFormatMessage('sure_discard_changes')}
                </div>
              ),
              onOk: async () => {
                setShowAuthDrawer(false);
                return true;
              },
            });
          } else {
            setShowAuthDrawer(false);
          }
        }}
        onOK={submit}
      />
    );
  };

  return (
    <Spin spinning={companyInfo === undefined}>
      <div
        className={classNames(styles.container, {
          [styles.none]: !companyInfo,
          [styles.pending]: companyInfo?.Status === CommonStatusStep.Pending,
          [styles.resolved]: companyInfo?.Status === CommonStatusStep.Resolve,
          [styles.rejected]: companyInfo?.Status === CommonStatusStep.Reject,
        })}
      >
        <div className={styles.title}>
          <div>
            <span>
              <span>{getFormatMessage('studio_auth')}</span>
              {(companyInfo?.Status === CommonStatusStep.Reject ||
                companyInfo?.Status === CommonStatusStep.Pending) && (
                <CommonTag
                  text={
                    companyInfo?.lastCertifiedType ===
                    StudioCertifiedType.Company
                      ? getFormatMessage('studio_auth_cooper')
                      : getFormatMessage('studio_auth_internal')
                  }
                />
              )}
            </span>
            <span className={styles.status}>{renderStatus()}</span>
          </div>
          {renderAuthButton(
            companyInfo,
            accountInfo,
            certType,
            () => {
              setShowAuthDrawer(true);
            },
            (val) => setCertType(val)
          )}
        </div>
        {renderContent()}
        {renderAuthDrawer()}
      </div>
    </Spin>
  );
};
